import { useState } from 'react'
import { SegmentControl } from '@forwardflow/ui-v2'
import {
   IContentRevision,
   IConversationDefinition,
   IEmailItem,
   IPersonaDefinition,
   IPhoneDefinition,
   IStrategyDefinition,
} from '../../interfaces/campaign-v2.types'
import { EmptyNote, ListCard, TextButton } from '../shared'

const CONTENT_LABELS = {
   phone: 'Call script',
   strategy: 'Outbound content group',
   conversation: 'Reply instructions',
   persona: 'Personality',
}

/** Read the loaded revision without changing the campaign or library. */
export const ContentPreview = ({
   revision,
   onClose,
   onEdit,
   shared = false,
}: {
   revision: IContentRevision
   onClose: () => void
   onEdit: () => void
   shared?: boolean
}) => (
   <div className="space-y-4" data-testid="content-preview">
      <div className="flex items-start justify-between gap-4">
         <div className="min-w-0">
            <h3 className="text-white font-semibold text-[15px] break-words">
               {revision.definition.name || CONTENT_LABELS[revision.kind]}
            </h3>
            <p className="text-xs text-white/50 mt-1">
               {CONTENT_LABELS[revision.kind]}
            </p>
         </div>
         <div className="flex items-center gap-4 shrink-0">
            <TextButton
               onClick={onClose}
               tone="muted"
               testId="content-preview-close"
            >
               Close
            </TextButton>
            <button
               type="button"
               className="cw-button primary"
               onClick={onEdit}
               data-testid="content-preview-edit"
            >
               {shared ? 'Customize' : 'Edit'}
            </button>
         </div>
      </div>

      {revision.kind === 'phone' && (
         <PhonePreview definition={revision.definition as IPhoneDefinition} />
      )}
      {revision.kind === 'strategy' && (
         <StrategyPreview
            key={revision.id}
            definition={revision.definition as IStrategyDefinition}
         />
      )}
      {revision.kind === 'conversation' && (
         <ListCard title="Reply instructions">
            <ReadText
               text={(revision.definition as IConversationDefinition).content}
               empty="No reply instructions yet."
            />
         </ListCard>
      )}
      {revision.kind === 'persona' && (
         <PersonaPreview definition={revision.definition as IPersonaDefinition} />
      )}
   </div>
)

const ReadText = ({ text, empty }: { text?: string | null; empty: string }) =>
   text?.trim() ? (
      <p className="px-4 py-4 whitespace-pre-wrap break-words text-sm leading-relaxed text-white/80 max-w-[80ch]">
         {text}
      </p>
   ) : (
      <EmptyNote>{empty}</EmptyNote>
   )

const PhonePreview = ({ definition }: { definition: IPhoneDefinition }) => {
   const sections = [...(definition.sections || [])].sort(
      (a, b) => a.order - b.order
   )
   return (
      <div className="space-y-4" data-testid="preview-phone">
         <ListCard title="Opening line">
            <ReadText
               text={definition.firstMessage}
               empty="No opening line yet."
            />
         </ListCard>
         {sections.length ? (
            sections.map((section, index) => (
               <ListCard
                  key={section.sectionId ?? index}
                  title={`${index + 1}. ${section.name || 'Call section'}`}
                  testId={`preview-phone-section-${index}`}
               >
                  <ReadText
                     text={section.content}
                     empty="No instructions in this section yet."
                  />
               </ListCard>
            ))
         ) : (
            <EmptyNote>No call sections yet.</EmptyNote>
         )}
      </div>
   )
}

type TChannel = 'sms' | 'email' | 'voicemail'
const CHANNELS: Array<{ id: TChannel; label: string; item: string }> = [
   { id: 'sms', label: 'Texts', item: 'Text' },
   { id: 'email', label: 'Emails', item: 'Email' },
   { id: 'voicemail', label: 'Voicemails', item: 'Voicemail' },
]

const StrategyPreview = ({ definition }: { definition: IStrategyDefinition }) => {
   const [channel, setChannel] = useState<TChannel>(
      () => CHANNELS.find((item) => definition[item.id]?.length)?.id || 'sms'
   )
   const selected = CHANNELS.find((item) => item.id === channel)!
   const items = definition[channel] || []
   return (
      <div className="space-y-4" data-testid="preview-strategy">
         <SegmentControl
            value={channel}
            onChange={(value) => setChannel(value as TChannel)}
            data={CHANNELS.map((item) => ({
               value: item.id,
               label: `${item.label} · ${definition[item.id]?.length || 0}`,
            }))}
            data-testid="preview-channel-tabs"
         />
         <div className="space-y-4" data-testid={`preview-series-${channel}`}>
            {items.length ? (
               items.map((item, index) => (
                  <ListCard
                     key={item.id ?? index}
                     title={`${selected.item} ${index + 1}`}
                     testId={`preview-${channel}-${index}`}
                  >
                     {channel === 'email' ? (
                        <>
                           <p className="px-4 py-3 text-sm font-medium text-white break-words">
                              {(item as IEmailItem).subject?.trim() || 'No subject'}
                           </p>
                           <ReadText
                              text={emailText(item as IEmailItem)}
                              empty="This email is empty."
                           />
                        </>
                     ) : (
                        <ReadText
                           text={'template' in item ? item.template : ''}
                           empty={`This ${selected.item.toLowerCase()} is empty.`}
                        />
                     )}
                  </ListCard>
               ))
            ) : (
               <EmptyNote>No {selected.label.toLowerCase()} in this group.</EmptyNote>
            )}
         </div>
      </div>
   )
}

/** Inert HTML is displayed as text; previewing never loads email resources. */
const emailText = (email: IEmailItem) => {
   if (email.contentType !== 'html' || !email.body) return email.body
   const template = document.createElement('template')
   template.innerHTML = email.body
   template.content.querySelectorAll('script, style').forEach((node) => node.remove())
   template.content.querySelectorAll('br').forEach((node) => node.replaceWith('\n'))
   template.content
      .querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6')
      .forEach((node) => node.append('\n'))
   return (template.content.textContent || '').replace(/\n{3,}/g, '\n\n').trim()
}

const PersonaPreview = ({ definition }: { definition: IPersonaDefinition }) => {
   const fields: Array<[keyof IPersonaDefinition['guidance'], string]> = [
      ['personaGuidance', 'Personality'],
      ['voiceStyle', 'Voice style'],
      ['speakingGuidance', 'How it speaks'],
      ['conversationStyle', 'How it converses'],
   ]
   const filled = fields.filter(([key]) => definition.guidance[key]?.trim())
   return (
      <div className="space-y-4" data-testid="preview-persona">
         {filled.length ? (
            filled.map(([key, label]) => (
               <ListCard key={key} title={label}>
                  <ReadText text={definition.guidance[key]} empty="No guidance yet." />
               </ListCard>
            ))
         ) : (
            <EmptyNote>No personality guidance yet.</EmptyNote>
         )}
      </div>
   )
}
